import { create } from "zustand";

/**
 * Per-connection state for the telemetry push pipeline (§07 of the FE
 * architecture spec). Deliberately in-memory only — no persist middleware,
 * no cross-tab sync. Each browser tab is its own JS execution context with
 * its own store instance; Tab 1 on Facility A must never see Tab 2's
 * connection state for Facility B, even in the same browser (§2.2).
 *
 * Written to by useTelemetryStream, read by TelemetryConnectionIndicator.
 */

export type TelemetryConnectionState =
  | "idle"
  | "connecting"
  | "live"
  | "reconnecting"
  | "disconnected"
  | "suspended"
  // Terminal state: the facility itself was deactivated (backend sends
  // event: bye with reason "deactivated", or the initial connect gets the
  // same dedicated 403). Distinct from "disconnected" because that state
  // keeps retrying — this one must not, since the endpoint will just keep
  // returning 403 until the facility is reactivated.
  | "deactivated"
  // Terminal state: the initial connect got a 403 that was NOT the
  // deactivated-facility case — the user was never granted access to this
  // facility at all. Also must never retry (PR #323 review: this previously
  // fell through to the same indefinite backoff loop as a transient error).
  | "forbidden"
  // Terminal state (JAA-1117): the stream endpoint 404'd — the facility no
  // longer exists (permanently deleted). Must never retry either.
  | "not_found";

export interface TelemetryConnectionEntry {
  state: TelemetryConnectionState;
  /** Epoch ms of the last event received on this connection, or null if none yet. */
  lastEventAt: number | null;
  /** Consecutive failed (re)connect attempts since the last successful "live". */
  attemptCount: number;
}

const DEFAULT_ENTRY: TelemetryConnectionEntry = {
  state: "live",
  lastEventAt: null,
  attemptCount: 0,
};

export interface TelemetryConnectionStoreState {
  connections: Record<number, TelemetryConnectionEntry>;

  setConnectionState: (facilityId: number, state: TelemetryConnectionState) => void;
  recordEvent: (facilityId: number, timestamp?: number) => void;
  incrementAttempt: (facilityId: number) => void;
  resetAttempts: (facilityId: number) => void;
  clearConnection: (facilityId: number) => void;
}

/** Reads the existing entry for a facility, or a fresh default if none exists yet. */
function entryOrDefault(
  connections: Record<number, TelemetryConnectionEntry>,
  facilityId: number,
): TelemetryConnectionEntry {
  return connections[facilityId] ?? DEFAULT_ENTRY;
}

export const useTelemetryConnectionStore = create<TelemetryConnectionStoreState>()((set) => ({
  connections: {},

  setConnectionState: (facilityId, state) =>
    set((s) => ({
      connections: {
        ...s.connections,
        [facilityId]: { ...entryOrDefault(s.connections, facilityId), state },
      },
    })),

  recordEvent: (facilityId, timestamp = Date.now()) =>
    set((s) => ({
      connections: {
        ...s.connections,
        [facilityId]: { ...entryOrDefault(s.connections, facilityId), lastEventAt: timestamp },
      },
    })),

  incrementAttempt: (facilityId) =>
    set((s) => {
      const existing = entryOrDefault(s.connections, facilityId);
      return {
        connections: {
          ...s.connections,
          [facilityId]: { ...existing, attemptCount: existing.attemptCount + 1 },
        },
      };
    }),

  resetAttempts: (facilityId) =>
    set((s) => ({
      connections: {
        ...s.connections,
        [facilityId]: { ...entryOrDefault(s.connections, facilityId), attemptCount: 0 },
      },
    })),

  clearConnection: (facilityId) =>
    set((s) => {
      const { [facilityId]: _removed, ...rest } = s.connections;
      return { connections: rest };
    }),
}));
